
import {defineComponent, PropType} from 'vue'
import {CatalogProductsRestService} from "@/services/rest/cpc-mgmt/CatalogProductsRestService";
import {PimProductsPagingModel} from "@/models/paging/PimProductsPagingModel";
import ProductListTile, {PRODUCT_SOURCE} from "@/components/UI/product/ProductListTile.vue";
import {trimLeadingZeroes} from "@/helpers/functions/number";
import {generateUUIDv4} from "@/helpers/functions/string";
import {FormMixin} from "@/components/base/FormMixin";
import AlertError2 from "@/components/UI/Bootstrap/Alert/AlertError2.vue";
import {UIStateDto} from "@/dtos/UIStateDto";
import {
  CatalogProductWithImageOverviewRestDtoModel
} from "@/models/api/pharma-cpc-product-mgmt/CatalogProductWithImageOverviewRestDtoModel";

export default defineComponent({
  name: "ProductTileView",
  mixins: [FormMixin],
  components: {AlertError2, ProductListTile},
  emits: ['update:modelValue'],
  props: {
    modelValue: {
      type: Array as PropType<string[]>,
      required: true,
      default: [] as string[],
    },
    useProductCodes: {
      type: Boolean,
      required: false,
      default: false
    },
    visible: {
      type: Boolean,
      required: true
    },
  },
  data() {
    return {
      componentUuid: generateUUIDv4(),
      codes: [] as string[],
      products: new Map() as Map<string, CatalogProductWithImageOverviewRestDtoModel>,
      pagingModel: PimProductsPagingModel.createWithDefaults(),
      previewOnly: true,
      initialNumberOfProducts: 25,
      maxNumberOfProducts: 500,
      dragIndex: -1,
      dropTargetIndex: -1,
      isDragging: false
    }
  },
  mounted() {
    this.reloadContent();
  },
  watch: {
    modelValue(_newVal: string[]) {
      this.reloadContent();
    },
    visible(_newVal) {
      this.reloadContent();
    }
  },
  methods: {
    async doReload(): Promise<void> {
      // No products should be retrieved if the component is not visible anyway.
      if (!this.visible) {
        return;
      }

      if (this.modelValue?.length) {
        this.codes = [...this.modelValue];
        if (!this.useProductCodes) {
          // Working with CNK codes.
          this.codes = this.codes.map((cnkCode: string) => trimLeadingZeroes(cnkCode));
        }
        await this.loadProducts();
      } else {
        this.codes = [];
      }
    },
    async loadProducts(): Promise<void> {
      if (!this.codes.length) {
        return;
      }

      // Get the list of the first x cnks to display, and filter out the ones we already have.
      const codesToFetch: string[] = this.codes.slice(
        0,
        Math.min(
          this.codes.length,
          this.previewOnly ? this.initialNumberOfProducts : this.maxNumberOfProducts
        )
      ).filter((code: string) => !this.products.has(code));

      // We only fetch the products we don't have in our map.  If there are no missing products, we do nothing.
      if (!codesToFetch.length) {
        return;
      }

      const productOverviewDtos: CatalogProductWithImageOverviewRestDtoModel[] =
        await CatalogProductsRestService.getInstance().findCatalogProductsManyAll(
          this.useProductCodes ? [] : codesToFetch,
          this.useProductCodes ? codesToFetch : [],
          this.$i18n.locale
        );
      if (this.useProductCodes) {
        productOverviewDtos.forEach((product: CatalogProductWithImageOverviewRestDtoModel) => {
          this.products.set(product.product.product_code, product);
        });
      } else {
        productOverviewDtos.forEach(product => {
          if (product.product.product_info.cnk_code) {
            this.products.set(trimLeadingZeroes(product.product.product_info.cnk_code), product);
          }
        });
      }
    },
    async showMore(): Promise<void> {
      this.previewOnly = false;
      await this.loadProducts();
    },
    async showLess(): Promise<void> {
      this.previewOnly = true;
    },
    getProduct(code: string): CatalogProductWithImageOverviewRestDtoModel | undefined {
      let product: CatalogProductWithImageOverviewRestDtoModel | undefined = this.products.get(code);
      if (!product) {
        product = CatalogProductWithImageOverviewRestDtoModel.createWithDefaults();
        if (this.useProductCodes) {
          product.product.product_code = code;
          product.product.product_source_code = PRODUCT_SOURCE.NOT_IN_CATALOG;
        } else {
          product.product.product_info.cnk_code = code;
          product.product.product_source_code = PRODUCT_SOURCE.NOT_IN_CATALOG;
        }
        this.products.set(code, product);
      }

      return product;
    },
    async deleteProduct(product: CatalogProductWithImageOverviewRestDtoModel): Promise<void> {
      await this.performGenericDelete(
        () => this.doDeleteProduct(product),
        this.afterDeleteProduct,
        this.pageUI as UIStateDto,
        false // Don't show a success message, as the change is only on the client side, no data is saved in the backend
      );
    },
    async doDeleteProduct(product: CatalogProductWithImageOverviewRestDtoModel): Promise<void> {
      if (this.useProductCodes) {
        this.codes = this.codes
          .filter((code: string) => code !== product.product.product_code);
      } else {
        this.codes = this.codes
          .filter((code: string) => code !== trimLeadingZeroes(product.product.product_info.cnk_code ?? ''));
      }
    },
    async afterDeleteProduct(): Promise<void> {
      await this.loadProducts();
      this.$emit('update:modelValue', this.codes);
    },
    handleDragStart(event: DragEvent, index: number): void {
      this.dragIndex = index;
      this.isDragging = true;
      event.dataTransfer!.effectAllowed = 'move';
      event.dataTransfer!.setData('text/plain', this.componentUuid);
    },
    handleDragOver(event: DragEvent, index: number): void {
      // Only allow dragover if we are dragging from THIS component
      if (!this.isDragging) {
        return;
      }

      this.dropTargetIndex = index;
      event.dataTransfer!.dropEffect = 'move';
    },
    handleDrop(event: DragEvent, index: number): void {
      // Only allow drop if we are dragging from THIS component
      if (!this.isDragging) {
        return;
      }

      if (this.dragIndex === -1 || this.dropTargetIndex === -1) {
        return;
      }

      const newCodes: string[] = [...this.codes] as string[];
      const draggedCode = newCodes.splice(this.dragIndex, 1)[0];
      const newIndex = this.dragIndex < index ? index - 1 : index;
      newCodes.splice(newIndex, 0, draggedCode);

      this.dragIndex = -1;
      this.dropTargetIndex = -1;
      this.isDragging = false;

      this.codes = newCodes;

      this.$emit('update:modelValue', this.codes);
    },
    handleDragEnd(): void {
      this.dragIndex = -1;
      this.dropTargetIndex = -1;
      this.isDragging = false;
    },
  },
  computed: {
    productCodesForGrid(): string[] {
      return this.codes.slice(
        0,
        Math.min(
          this.codes.length,
          this.previewOnly ? this.initialNumberOfProducts : this.maxNumberOfProducts
        )
      );
    },
  },
});
